import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

type RetryConfig = InternalAxiosRequestConfig & { _csrfRetried?: boolean };

export interface CsrfRetryOptions {
  /** Endpoint that refreshes the XSRF-TOKEN cookie. Default: /sanctum/csrf-cookie */
  csrfCookieUrl?: string;
  /** Called when a 419 survives one retry — the session is dead, not just the token. */
  onSessionDead?: () => void;
}

/**
 * Canonical Sanctum CSRF (419) recovery for an axios instance (W4 D5).
 * Single-flight token refresh + retry-once + escalate-on-repeat. No cookie
 * deletion (the refresh GET overwrites XSRF-TOKEN; the HttpOnly session cookie
 * is untouchable from JS). Retrying is safe — a 419 is rejected before the
 * request is processed, so there is no double-submit. Never retries 5xx.
 */
export function installCsrfRetry(instance: AxiosInstance, options: CsrfRetryOptions = {}): void {
  const csrfUrl = options.csrfCookieUrl ?? '/sanctum/csrf-cookie';
  let inFlight: Promise<unknown> | null = null;

  const refreshToken = (): Promise<unknown> => {
    // Single-flight: concurrent 419s share ONE refresh, then each retries.
    // baseURL: '' overrides the instance default (e.g. '/api' on tenantApi) so
    // the refresh always hits the site-root path — axios buildFullPath('', url)
    // returns url unchanged, giving '/sanctum/csrf-cookie' not '/api/sanctum/...'.
    inFlight ??= instance.get(csrfUrl, { baseURL: '' }).finally(() => {
      inFlight = null;
    });
    return inFlight;
  };

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const status = error.response?.status;
      const config = error.config as RetryConfig | undefined;

      if (status === 419 && config) {
        if (!config._csrfRetried) {
          config._csrfRetried = true;
          try {
            await refreshToken();
            return await instance.request(config);
          } catch (retryError) {
            // If the retry itself was a 419, the nested interceptor invocation
            // already called onSessionDead — do not call it again here.
            const retryStatus = (retryError as AxiosError)?.response?.status;
            if (retryStatus !== 419) {
              options.onSessionDead?.();
            }
            return Promise.reject(retryError);
          }
        }
        // Already retried and still 419 → the session is dead, not the token.
        options.onSessionDead?.();
      }

      return Promise.reject(error);
    },
  );
}
